import React from 'react';
import { Row, Col, Card, Statistic, Typography, Spin, List, Tag, Space } from 'antd';
import {
  TeamOutlined,
  BankOutlined,
  DesktopOutlined,
  AuditOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ToolOutlined,
  ArrowUpOutlined,
  ClockCircleOutlined,
} from '@ant-design/icons';
import { useDashboardStats } from '@/hooks/useStats';
import { useSessions } from '@/hooks/useSessions';

const { Title, Text } = Typography;

const Dashboard: React.FC = () => {
  const { data: stats, isLoading } = useDashboardStats();
  const { data: sessionsData } = useSessions({ page: 1, status: 'CONNECTED' });

  const activeSessions = sessionsData?.results || [];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <Title level={4} style={{ margin: 0 }}>Dashboard</Title>
        <Text type="secondary">
          <ClockCircleOutlined /> Última atualização: {new Date().toLocaleTimeString('pt-BR')}
        </Text>
      </div>
      
      <Spin spinning={isLoading}>
        {/* Cards de Estatísticas Principais */}
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12} lg={6}>
            <Card hoverable>
              <Statistic
                title="Usuários Ativos"
                value={stats?.users || 0}
                prefix={<TeamOutlined />}
                valueStyle={{ color: '#1890ff' }}
                suffix={<ArrowUpOutlined style={{ fontSize: 14, color: '#52c41a' }} />}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Card hoverable>
              <Statistic
                title="Empresas Ativas"
                value={stats?.companies || 0}
                prefix={<BankOutlined />}
                valueStyle={{ color: '#52c41a' }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Card hoverable>
              <Statistic
                title="Total Dispositivos"
                value={stats?.devices || 0}
                prefix={<DesktopOutlined />}
                valueStyle={{ color: '#722ed1' }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Card hoverable>
              <Statistic
                title="Sessões Ativas"
                value={stats?.active_sessions || 0}
                prefix={<AuditOutlined />}
                valueStyle={{ color: '#faad14' }}
              />
            </Card>
          </Col>
        </Row>

        {/* Cards de Status dos Dispositivos */}
        <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
          <Col xs={24} sm={8}>
            <Card>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Statistic
                  title="Online"
                  value={stats?.online_devices || 0}
                  valueStyle={{ color: '#52c41a', fontSize: 28 }}
                />
                <CheckCircleOutlined style={{ fontSize: 40, color: '#52c41a', opacity: 0.3 }} />
              </div>
              <div style={{ marginTop: 8 }}>
                <div style={{ 
                  height: 8, 
                  background: '#f0f0f0', 
                  borderRadius: 4,
                  overflow: 'hidden'
                }}>
                  <div style={{ 
                    height: '100%', 
                    width: `${stats?.devices ? (stats.online_devices / stats.devices * 100) : 0}%`,
                    background: '#52c41a',
                    borderRadius: 4,
                    transition: 'width 0.3s'
                  }} />
                </div>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {stats?.devices ? Math.round(stats.online_devices / stats.devices * 100) : 0}% do total
                </Text>
              </div>
            </Card>
          </Col>
          <Col xs={24} sm={8}>
            <Card>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Statistic
                  title="Offline"
                  value={stats?.offline_devices || 0}
                  valueStyle={{ color: '#ff4d4f', fontSize: 28 }}
                />
                <CloseCircleOutlined style={{ fontSize: 40, color: '#ff4d4f', opacity: 0.3 }} />
              </div>
              <div style={{ marginTop: 8 }}>
                <div style={{ 
                  height: 8, 
                  background: '#f0f0f0', 
                  borderRadius: 4,
                  overflow: 'hidden'
                }}>
                  <div style={{ 
                    height: '100%', 
                    width: `${stats?.devices ? (stats.offline_devices / stats.devices * 100) : 0}%`,
                    background: '#ff4d4f',
                    borderRadius: 4,
                    transition: 'width 0.3s'
                  }} />
                </div>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {stats?.devices ? Math.round(stats.offline_devices / stats.devices * 100) : 0}% do total
                </Text>
              </div>
            </Card>
          </Col>
          <Col xs={24} sm={8}>
            <Card>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Statistic
                  title="Manutenção"
                  value={stats?.maintenance_devices || 0}
                  valueStyle={{ color: '#faad14', fontSize: 28 }}
                />
                <ToolOutlined style={{ fontSize: 40, color: '#faad14', opacity: 0.3 }} />
              </div>
              <div style={{ marginTop: 8 }}>
                <div style={{ 
                  height: 8, 
                  background: '#f0f0f0', 
                  borderRadius: 4,
                  overflow: 'hidden'
                }}>
                  <div style={{ 
                    height: '100%', 
                    width: `${stats?.devices ? (stats.maintenance_devices / stats.devices * 100) : 0}%`,
                    background: '#faad14',
                    borderRadius: 4,
                    transition: 'width 0.3s'
                  }} />
                </div>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {stats?.devices ? Math.round(stats.maintenance_devices / stats.devices * 100) : 0}% do total
                </Text>
              </div>
            </Card>
          </Col>
        </Row>

        {/* Sessões Ativas */}
        <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
          <Col xs={24} lg={16}>
            <Card title="Sessões Ativas" extra={<Tag color="green">{activeSessions.length} ativas</Tag>}>
              {activeSessions.length > 0 ? (
                <List
                  dataSource={activeSessions.slice(0, 5)}
                  renderItem={(session: any) => (
                    <List.Item>
                      <List.Item.Meta
                        title={
                          <Space>
                            <Text strong>{session.source_device_name}</Text>
                            <Text type="secondary">→</Text>
                            <Text strong>{session.target_device_name}</Text>
                          </Space>
                        }
                        description={
                          <Space>
                            <Text type="secondary">Iniciado por: {session.initiated_by_name}</Text>
                            <Tag color="green">Conectado</Tag>
                          </Space>
                        }
                      />
                      <Text type="secondary">
                        {new Date(session.started_at).toLocaleTimeString('pt-BR')}
                      </Text>
                    </List.Item>
                  )}
                />
              ) : (
                <div style={{ textAlign: 'center', padding: 40, color: '#999' }}>
                  <AuditOutlined style={{ fontSize: 40, marginBottom: 16 }} />
                  <div>Nenhuma sessão ativa no momento</div>
                </div>
              )}
            </Card>
          </Col>
          <Col xs={24} lg={8}>
            <Card title="Resumo de Sessões">
              <div style={{ marginBottom: 16 }}>
                <Text type="secondary">Total de Sessões</Text>
                <Title level={3} style={{ margin: 0 }}>{stats?.total_sessions || 0}</Title>
              </div>
              <div style={{ marginBottom: 16 }}>
                <Text type="secondary">Sessões Ativas Agora</Text>
                <Title level={3} style={{ margin: 0, color: '#52c41a' }}>{stats?.active_sessions || 0}</Title>
              </div>
              <div>
                <Text type="secondary">Média por Dia</Text>
                <Title level={3} style={{ margin: 0 }}>{stats?.total_sessions ? Math.round(stats.total_sessions / 30) : 0}</Title>
              </div>
            </Card>
          </Col>
        </Row>
      </Spin>
    </div>
  );
};

export default Dashboard;
